import React, { useContext, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ReloadOutlined } from '@ant-design/icons';
import { Popover, Input, Button, Tooltip, Divider } from 'antd';
import { HeaderWrapper, PopoverContent, ApplyIcon, MetricTitle } from './styled';
import {
  ERROR_THRESHOLD_TYPE,
  ERROR_MSG,
  percentageValidation,
  isInstantFilter,
} from '../../utils/constants';
import {
  contentTableDataSelector,
  thresholdCellConfigSelector,
} from '@store/noc/threshold/selectors';
import ParentContext from '@src/pages/Noc/Threshold/context';
import { getMetricMetaByKey } from '@utils/metrics';
import { adminRoleSelector, nocModeSelector } from '@store/noc/base/selectors';
import { NOC_MODE_FILTERS, NOC_MODE_TRENDS } from '@utils/constants';
import { filterSelector as trendsViewFilter } from '@store/noc/trendsData/selectors';
import { filterSelector as filterViewFilters } from '@store/noc/filtersData/selectors';

const { Search } = Input;

interface Props {
  title: string;
  dataKey: string;
}

function validation(v: string, unit: string) {
  if (!v) return '';

  let errorMsg = '';
  const value = Number(v);
  if (isNaN(value)) {
    errorMsg = ERROR_MSG[ERROR_THRESHOLD_TYPE.GENERAL];
  } else if (unit === '%' && (value >= 100 || value <= 0)) {
    errorMsg = ERROR_MSG[ERROR_THRESHOLD_TYPE.OUT_OF_RANGE];
  } else if (value <= 0) {
    errorMsg = ERROR_MSG[ERROR_THRESHOLD_TYPE.LESS_EQUAL_ZERO];
  }
  return errorMsg;
}

const MetricHeader = ({ title, dataKey }: Props) => {
  const { handleThresholdUpdate } = useContext(ParentContext);
  const mode = useSelector(nocModeSelector);
  const isAdmin = useSelector(adminRoleSelector);
  const cellConfigStore = useSelector(thresholdCellConfigSelector);
  const trendFilter = useSelector(trendsViewFilter);
  const filters = useSelector(filterViewFilters);
  const contentData = useSelector(contentTableDataSelector) as Array<Record<string, any>>;

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const disabled = useMemo(() => {
    if (!isAdmin) {
      return true;
    }
    if (mode === NOC_MODE_TRENDS && !cellConfigStore.thresholdDisabled) {
      return isInstantFilter(trendFilter);
    }
    return cellConfigStore.thresholdDisabled;
  }, [trendFilter, cellConfigStore.thresholdDisabled, mode, isAdmin]);

  const unit = useMemo(() => getMetricMetaByKey({ metricKey: dataKey }).unit, [dataKey]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      setErrorMsg(validation(e.target.value, unit));
    },
    [unit],
  );

  const onApply = useCallback(
    (data) => {
      const dataRows =
        mode === NOC_MODE_FILTERS
          ? contentData.filter((rowData) => {
              const filter = filters.find((i) => i.name === rowData.RowKeys);
              return !isInstantFilter(filter); // instant filter is not allowed
            })
          : contentData;
      const configs = dataRows.map((row) => {
        const item = row[dataKey];
        return {
          identifier: item.identifier || dataKey,
          metric: dataKey,
          data,
        };
      });
      const validate = unit === '%' ? percentageValidation : undefined;
      handleThresholdUpdate({ type: 'thresholds', configs, validate });
    },
    [unit, contentData, dataKey, handleThresholdUpdate, filters, mode],
  );

  const content = useMemo(() => {
    const disabled = !!errorMsg || !value;
    return (
      <PopoverContent>
        <Tooltip title={errorMsg} open={!!errorMsg}>
          <Search
            placeholder="apply to entire column"
            allowClear
            value={value}
            status={errorMsg ? 'error' : undefined}
            enterButton={<Button disabled={disabled}>Apply</Button>}
            onChange={onChange}
            onSearch={() => {
              if (!disabled) onApply({ custom: value, isCustomSelected: true });
            }}
          />
        </Tooltip>
        <Divider style={{ margin: '20px 0' }} />
        <Button
          block
          icon={<ReloadOutlined rotate={90} />}
          onClick={() => onApply({ isCustomSelected: false })}
        >
          Reset to Conviva Threshold
        </Button>
      </PopoverContent>
    );
  }, [errorMsg, value, onChange, onApply]);

  return (
    <HeaderWrapper>
      <MetricTitle tooltipText={title}>{title}</MetricTitle>
      <Popover
        open={open}
        content={content}
        trigger="click"
        onOpenChange={(open) => {
          if (disabled) return;
          if (!open) {
            setValue('');
            setErrorMsg('');
          }
          setOpen(open);
        }}
      >
        <Button disabled={disabled} type="text" icon={<ApplyIcon />} />
      </Popover>
    </HeaderWrapper>
  );
};

export default MetricHeader;
